import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  isSupportedLanguage,
  LANGUAGE_STORAGE_KEY,
  type SupportedLanguage,
} from "@/i18n";

/**
 * Keeps the active i18n language in sync with the user's stored profile preference.
 *
 * - On login: pulls `profiles.language` and applies it (overrides browser detection).
 * - On manual change: persists the new language back to the profile.
 *
 * Returns helpers for changing the language with persistence.
 */
export function useLanguageSync() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const lastSyncedUserId = useRef<string | null>(null);

  // Pull preference from profile on auth change
  useEffect(() => {
    if (!user?.id) {
      lastSyncedUserId.current = null;
      return;
    }
    if (lastSyncedUserId.current === user.id) return;
    lastSyncedUserId.current = user.id;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("language")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled || error || !data?.language) return;
      if (isSupportedLanguage(data.language) && data.language !== i18n.language) {
        await i18n.changeLanguage(data.language);
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, data.language);
        } catch {
          /* ignore */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, i18n]);

  const changeLanguage = async (lang: SupportedLanguage) => {
    await i18n.changeLanguage(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    if (user?.id) {
      await supabase
        .from("profiles")
        .update({ language: lang })
        .eq("user_id", user.id);
    }
  };

  return {
    language: (i18n.language?.split("-")[0] ?? "pt") as SupportedLanguage,
    changeLanguage,
  };
}
